import { z } from 'zod';
import { contentChoiceSchema } from './choice.js';
import { contentBaseShape } from './common.js';
/**
 * Правило системы записью контента (SRD 5.2, «Character Creation»).
 *
 * Числа отсюда — стандартный набор характеристик, бюджет покупки,
 * кости броска, языки — такие же числа источника, как урон меча,
 * и живут они там же: в `content/srd/`, со ссылкой на страницу.
 * Захардкодить «27 очков» в движке значило бы завести второе место,
 * где хранятся правила, и однажды они разойдутся.
 *
 * `effects` этой записи — базовые правила листа («КД начинается
 * с 10 плюс модификатор Ловкости»). В свёртке они получают источник
 * `ruleset` (rules-engine.md, раздел 6).
 */
/** Стоимость очка покупки: счёт → сколько стоит (таблица источника). */
export const pointCostSchema = z.strictObject({
    score: z.number().int().positive(),
    cost: z.number().int().nonnegative(),
});
export const abilityMethodsSchema = z.strictObject({
    /** «Use the following six scores»: 15, 14, 13, 12, 10, 8. */
    standardArray: z.array(z.number().int().positive()).length(6),
    pointBuy: z.strictObject({
        /** «You have 27 points to spend». */
        budget: z.number().int().positive(),
        costs: z.array(pointCostSchema).min(2),
    }),
    /**
     * «Roll four d6s and record the total of the highest three dice.
     * Do this five more times». Движок кости не бросает (D21.4): числа
     * приходят снаружи, отсюда берутся только границы допустимого.
     */
    roll: z.strictObject({
        dice: z.number().int().positive(),
        die: z.number().int().positive(),
        keep: z.number().int().positive(),
        count: z.number().int().positive(),
    }),
});
/**
 * «Increase one of those scores by 2 and a different one by 1,
 * or increase all three by 1. None of these increases can raise
 * a score above 20» — прибавки предыстории.
 */
export const abilityIncreaseRuleSchema = z.strictObject({
    /** Допустимые раскладки прибавок: [2, 1] и [1, 1, 1]. */
    patterns: z.array(z.array(z.number().int().positive()).min(1)).min(1),
    maxScore: z.number().int().positive(),
});
/**
 * Грузоподъёмность (SRD 5.2, глоссарий, «Carrying Capacity»,
 * с. 178 PDF).
 *
 * Таблица источника целиком: сколько персонаж несёт и сколько тащит,
 * волочит или поднимает — в фунтах на очко Силы, по размеру существа.
 * Множитель зависит от размера, поэтому это таблица, а не число.
 *
 * Единственное последствие превышения, которое SRD называет: «While
 * dragging, lifting, or pushing weight in excess of the maximum weight
 * you can carry, your Speed can be no more than 5 feet». Ступенчатой
 * нагрузки с промежуточными штрафами в SRD 5.2 нет, и выдумывать её
 * мы не будем.
 */
export const carryingCapacityRowSchema = z.strictObject({
    size: z.enum(['tiny', 'small', 'medium', 'large', 'huge', 'gargantuan']),
    /** «Str. × 15 lb.» — фунтов на очко Силы. */
    carryPerStrength: z.number().positive(),
    /** Столбец «Drag/Lift/Push» той же таблицы. */
    dragPerStrength: z.number().positive(),
});
export const carryingCapacitySchema = z.strictObject({
    bySize: z.array(carryingCapacityRowSchema).min(1),
});
/**
 * Строка таблицы «Character Advancement» (SRD 5.2, с. 22).
 *
 * Порог опыта и бонус мастерства по уровню — числа документа,
 * и живут они там же, где остальные: в записи контента. Мастер
 * начисляет опыт, лист предлагает повышение по достижении порога
 * (D31), и ни одной из двух сторон не полагается знать таблицу
 * наизусть.
 */
export const advancementRowSchema = z.strictObject({
    level: z.number().int().min(1).max(20),
    /** «When your XP total equals or exceeds a number … you reach the level». */
    xp: z.number().int().nonnegative(),
    proficiencyBonus: z.number().int().positive(),
});
/**
 * Пассивное Восприятие: «Passive Perception = 10 + Wisdom
 * (Perception) check modifier» (с. 21).
 *
 * Пассивное значение в SRD 5.2 ровно одно, и формула у него одна.
 * Десятка лежит здесь, а не в движке: в движке она была бы вторым
 * местом хранения правила.
 */
export const passivePerceptionSchema = z.strictObject({
    base: z.number().int().nonnegative(),
    /** Навык, чей модификатор складывается с основой: `srd:perception`. */
    skill: z.string().regex(/^srd:[a-z0-9_]+$/),
});
export const ruleSchema = z.strictObject({
    ...contentBaseShape,
    type: z.literal('rule'),
    mechanics: z.strictObject({
        abilityMethods: abilityMethodsSchema,
        abilityIncrease: abilityIncreaseRuleSchema,
        /**
         * Прибавка при повышении уровня: черта Ability Score Improvement
         * (с. 88). Раскладки у неё свои — «одна на 2 либо две по 1», —
         * и путать их с прибавкой предыстории нельзя.
         */
        abilityScoreImprovement: abilityIncreaseRuleSchema,
        /** Языки, известные каждому персонажу: «Every player character knows Common». */
        knownLanguages: z.array(z.string().min(1)),
        /** Таблица уровней: порог опыта и бонус мастерства (с. 22). */
        advancement: z.array(advancementRowSchema).length(20),
        /** Формула пассивного Восприятия (с. 21). */
        passivePerception: passivePerceptionSchema,
        /** Сколько персонаж несёт: таблица источника, а не число в коде. */
        carryingCapacity: carryingCapacitySchema,
        /**
         * Выборы, которые правило создания задаёт само: языки. Тот же
         * формат, что у класса и вида, — визард не различает, кто объявил
         * выбор.
         */
        choices: z.array(contentChoiceSchema),
    }),
});
//# sourceMappingURL=rule.js.map